import React, {useContext, useMemo, useState} from "react";
import {UserHomepageControllerContext, UserHomepageStateContext} from "../../../../../index";
import UserHomepageControlPanelTabCardTitleSection from "./title-section";
import {UserHomepageEventEmitter} from "../../../../../models";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import {CircularProgress, Fade, SvgIcon} from "@mui/material";
import {ReactComponent as HorizontalDragIcon} from "../../../../../../../../assets/images/notifications/horizontal-drag.svg";
import IconButton from "../../../../../../../components/base/button/icon_button";
import {Edit} from "@mui/icons-material";
import {ReactComponent as RemoveIcon} from "../../../../../../../../assets/images/remove.svg";
import {preventEventPropagation} from "../../../../../../../../core/services/utils/utils";
import Api from "../../../../../../../../core/services/api/api";
import useCanManageHomepage from "../../../../../hooks/use-can-manage-homepage";
import useAccessLevels from "../../../../../../../hooks/use-access-levels";

const UserHomepageControlPanelTabCard = ({tab, dragging, sortable}) => {
    const controller = useContext(UserHomepageControllerContext);
    const state = useContext(UserHomepageStateContext);
    const [inEditMode, setInEditMode] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [editing, setEditing] = useState(false);
    const isMounted = useIsMounted();
    const canManageHomepage = useCanManageHomepage();
    const {packages} = useAccessLevels();
    const hasHomepageManageFlag =
        packages.clientPlatform.manageWebHomepageEditions ||
        packages.clientPlatform.managePhoneHomepageEditions;

    const stateId = useMemo(() => state?.id, [state?.id]);
    const currentTab = useMemo(() => state?.currentData?.tabs?.find(e => e.id === tab.id), [tab.id, state?.currentData?.tabs])
    const disabled = useMemo(() => removing || editing, [removing, editing]);


    /**
     * Updates the title of the current tab in the server.
     *
     * @param {string} title
     * @return {Promise<boolean | undefined>}
     */
    const updateTabTitle = async (title) => {
        if (!canManageHomepage || !hasHomepageManageFlag) return;
        setEditing(true);
        const forApi = {
            id: tab?.id,
            name: title,
        }
        const response = await Api.updateHomepageEditionTab(forApi);
        if (!isMounted())
            return;
        setEditing(false);
        return response?.resultFlag;
    }

    /**
     * Removes the current tab from the home page edition.
     */
    const removeTab = async () => {
        if (!canManageHomepage || !hasHomepageManageFlag) return;
        setRemoving(true);
        const tabId = tab.id;
        const response = await Api.removeHomepageEditionTab(tabId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            controller.removeTab(tabId, UserHomepageEventEmitter.controlPanel, state);
        }
        setRemoving(false);
    }

    /**
     * Invokes the appropriate controller callback for the click of the tab card.
     */
    const onTabClicked = () => {
        if (dragging)
            return;
        controller.selectTab(tab?.id, UserHomepageEventEmitter.controlPanel);
    }

    /**
     * Gets users confirmation before removing the current tab entirely.
     */
    const getRemoveConfirmation = () => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure that you want to remove this tab? by doing so, all the components within this tab gets
                    removed as well.
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removeTab(),
        );
    }

    /**
     * The title section of the tag card
     * @type {JSX.Element}
     */
    const title = useMemo(() => (
        <UserHomepageControlPanelTabCardTitleSection
            tabId={tab.id}
            disabled={disabled}
            currentName={currentTab.name}
            inEditMode={inEditMode}
            exitEditMode={() => setInEditMode(false)}
            update={updateTabTitle}
        />
    ), [tab.id, currentTab.name, inEditMode, disabled])

    /**
     * The header section of the tag card
     * @type {JSX.Element}
     */
    const header = useMemo(() => (
        <div className={'tab-header'}>
            <Fade in={sortable} className={'mx-1 handle'}>
                <SvgIcon component={HorizontalDragIcon}/>
            </Fade>
            <div className={'d-flex align-items-center justify-content-end flex-grow-1'}>
                <Fade in={!inEditMode}>
                    <div className={'d-flex align-items-center justify-content-end'}>
                        <IconButton
                            disabled={disabled}
                            className={"mx-1 ml-2"}
                            color={'paper.secondary'}
                            onPressed={(e) => preventEventPropagation(e, () => setInEditMode(true))}
                        >
                            <Edit className={"icon normal"}/>
                        </IconButton>
                        <IconButton
                            onPressed={(e) => preventEventPropagation(e, getRemoveConfirmation)}
                            disabled={disabled}
                            color={'paper.error'}
                        >
                            {
                                removing
                                    ? <CircularProgress size={15}/>
                                    : <SvgIcon component={RemoveIcon} className='icon normal'/>
                            }
                        </IconButton>
                    </div>
                </Fade>
                <div className={'count-container'}>
                    <p>
                        {tab?.components?.length ?? 0}
                    </p>
                </div>
            </div>
        </div>
    ), [sortable, tab?.components?.length, inEditMode, disabled, removing])

    return (
        <div
            className={'user-homepage-control-panel-tab-card'}
            id={tab.controlPanelElementId}
            onClick={onTabClicked}
        >
            {header}
            {title}
        </div>
    );
}

export default UserHomepageControlPanelTabCard;
